import {
  Box,
  cn,
  Flex,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@100mslive/hms-ai-ui";
import {
  ArrowSeparateVertical,
  CheckCircle,
  ClockRotateRight,
  LogOut,
  Settings,
  SidebarCollapse,
  User,
  UserCircle,
} from "iconoir-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import type { AppDispatch, RootState } from "src/store";

import { authProtectedRoutes } from "../../app/index";
import LogoText from "../../assets/company/100ms-logo-text.png";
import Logo from "../../assets/company/hms-header-logo.png";
import { fetchUserOrganizationDetails } from "../../services/organization";
import { InfoDialogBox } from "../components/InfoDialogBox";

export function AuthSidebar() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [expandedPopoverOpen, setExpandedPopoverOpen] = useState(false);
  const [, setCollapsedPopoverOpen] = useState(false);

  const [showRoleInfo, setShowRoleInfo] = useState(false);
  const {role_name: role, roles, email, name} = useSelector(
    (store: RootState) => store.user.userOganizationDetails,
  );

  const location = useLocation();
  const path = location.pathname;
  const dispatch: AppDispatch = useDispatch();

  const { userOganizationDetails } = useSelector(
    (state: RootState) => state.user,
  );


  const roleDetails = roles?.find(roleItem => roleItem.name === role);


  useEffect(() => {
    dispatch(fetchUserOrganizationDetails());
  }, [dispatch]);

  const handleMouseEnter = useCallback(() => {
    if (isCollapsed) {
      setIsHovered(true);
    }
  }, [isCollapsed]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    if (isCollapsed) {
      setCollapsedPopoverOpen(false);
      setExpandedPopoverOpen(false);
    }
  }, [isCollapsed]);

  const toggleCollapse = useCallback(() => {
    setIsCollapsed(prev => !prev);
    setCollapsedPopoverOpen(false);
    setExpandedPopoverOpen(false);
  }, []);

  const isExpanded = !isCollapsed || (isCollapsed && isHovered);

  const filteredRoutes = useMemo(
    () => authProtectedRoutes.filter(r => r.showInSidebar),
    [authProtectedRoutes],
  );

  // Close popovers when route changes
  useEffect(() => {
    setCollapsedPopoverOpen(false);
    setExpandedPopoverOpen(false);
  }, [location]);

  return (
    <Flex
      className={`overflow-y-auto bg-bg-tertiary transition-all duration-500 flex-shrink-0
        ${isExpanded ? "w-[17.5rem]" : "w-20"}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <SidebarMenu className="w-full">
        <SidebarHeader className="bg-bg-tertiary p-5">
          <Flex
            align="center"
            className={`${isExpanded ? "justify-between" : "justify-center"}`}
          >
            <Link to="/call-flows">
              <div className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-100 active:bg-gray-200 transition-colors cursor-pointer">
                <div className="bg-bg-primary p-0.5 rounded-md h-6 w-6 shrink-0">
                  <img src={Logo} alt="100ms logo" />
                </div>
                <img
                  src={LogoText}
                  alt="100ms"
                  className={`w-14 h-4 transition-opacity duration-100 shrink-0 ${
                    isExpanded ? "opacity-100" : "hidden opacity-0"
                  }`}
                />
              </div>
            </Link>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={toggleCollapse}
                    className={`hover:text-txt-placholder-subtle text-txt-placeholder focus:text-txt-placeholder p-1
                      transition-all duration-300 ${
                        isExpanded ? "opacity-100 w-auto" : "hidden"
                      }`}
                  >
                    <SidebarCollapse
                      className={`transform transition-transform duration-200 ${
                        isCollapsed ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  align="center"
                  sideOffset={4}
                  className=""
                >
                  {isCollapsed ? "Lock Sidebar" : "Hide Sidebar"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Flex>
        </SidebarHeader>
        <SidebarContent className="bg-bg-tertiary px-4">
          {filteredRoutes.map(route => {
            const active = path.includes(route.pathCategory);

            if (isExpanded) {
              return (
                <Box
                  className={`${
                    active ? "bg-bg-primary" : ""
                  } px-3 py-2 w-full rounded-lg`}
                  key={route.path}
                >
                  <Link to={route.path}>
                    <Flex
                      className="gap-2 w-full cursor-pointer"
                      align="center"
                    >
                      {route.icon ? (
                        active && route.solidIcon ? (
                          <route.solidIcon
                            // @ts-ignore
                            className="text-txt-brand-tertiary min-w-[20px]"
                            height={20}
                            width={20}
                          />
                        ) : (
                          <route.icon
                            // @ts-ignore
                            className="text-txt-tertiary min-w-[20px]"
                            height={20}
                            width={20}
                          />
                        )
                      ) : (
                        <></>
                      )}

                      <p
                        className={cn(
                          "text-md font-semibold transition-opacity duration-150 whitespace-nowrap",
                          {
                            "opacity-100": isExpanded,
                            "opacity-0": !isExpanded,
                            "text-txt-secondary-hover": active,
                            "text-txt-primary": !active,
                          },
                        )}
                      >
                        {route.title}
                      </p>
                    </Flex>
                  </Link>
                </Box>
              );
            }

            return (
              <Box
                className={`${
                  active ? "bg-bg-primary" : ""
                } px-2 py-2 w-full   rounded-lg `}
                key={route.path}
              >
                <Link to={route.path}>
                  {route.icon &&
                    (active && route.solidIcon ? (
                      <route.solidIcon
                        // @ts-ignore
                        className="text-txt-brand-tertiary min-w-[20px] mx-auto"
                        height={20}
                        width={20}
                      />
                    ) : (
                      <route.icon
                        // @ts-ignore
                        className="text-txt-tertiary min-w-[20px] mx-auto"
                        height={20}
                        width={20}
                      />
                    ))}
                </Link>
              </Box>
            );
          })}
        </SidebarContent>

        <SidebarFooter className="p-4">
          {isExpanded ? (
            <Popover
              open={expandedPopoverOpen}
              onOpenChange={setExpandedPopoverOpen}
            >
              <PopoverTrigger>
                <Flex
                  justify="between"
                  className="p-3 rounded-lg border border-bd-secondary bg-bg-secondary"
                >
                  <Flex
                    direction="column"
                    align="start"
                    className="min-w-0 w-full"
                  >
                    <p className="text-sm text-txt-primary font-medium truncate w-full text-left">
                      {name}
                    </p>
                    <p className="text-sm text-txt-tertiary truncate w-full text-left">
                      {email}
                    </p>
                  </Flex>
                  <ArrowSeparateVertical className="my-auto" />
                </Flex>
              </PopoverTrigger>

              <PopoverContent
                className="w-full p-0 py-1"
                sideOffset={-4}
                style={{
                  borderRadius: "8px",
                  width: "calc(var(--radix-popover-trigger-width) + 16px)",
                  boxShadow: `
                    0px 2px 2px -1px var(--ColorsEffectsShadowsshadow-lg03),
                    0px 4px 6px -2px var(--ColorsEffectsShadowsshadow-lg02),
                    0px 12px 16px -4px var(--ColorsEffectsShadowsshadow-lg01)
                  `,
                }}
              >
                {role === "Admin" ? (
                  <Link to="/organization">
                    <div className="px-4 py-2 hover:bg-bg-primary-hover">
                      <Flex align="center" className="gap-2">
                        <Settings strokeWidth={2} />
                        <p className="text-sm font-semibold text-txt-tertiary">
                          Organization
                        </p>
                      </Flex>
                    </div>
                  </Link>
                ) : (
                  ""
                )}
                <Link to="/feedback">
                  <Flex
                    align="center"
                    className="gap-2 py-2 px-4 cursor-pointer hover:bg-bg-primary-hover border-b text-txt-tertiary"
                  >
                    <ClockRotateRight strokeWidth={2} />
                    <p className="text-sm font-semibold text-txt-tertiary">
                      Feedback History
                    </p>
                  </Flex>
                </Link>

                {userOganizationDetails.display_name ? (
                  <div className="px-2">
                    <Flex direction="column" className="gap-1 p-2">
                      <p className="text-xs">Organization</p>

                      <p className="text-sm font-semibold text-txt-tertiary">
                        {userOganizationDetails.display_name}
                      </p>
                    </Flex>
                  </div>
                ) : (
                  ""
                )}

                <InfoDialogBox
                  title="Role details"
                  isOpen={showRoleInfo}
                  onClose={() => setShowRoleInfo(false)}
                  description={
                    roleDetails ? (
                      <Box>
                        <p className="font-semibold">{roleDetails.name}</p>
                        <p className="text-sm">{roleDetails.description}</p>
                        <div className="mt-2">
                          <p className="font-semibold mb-2">Permissions:</p>

                          {roleDetails.permissions?.map(permission =>
                            permission.description !== "-" ? (
                              <p
                                className="text-sm flex items-center gap-2"
                                key={permission.permission_name}
                              >
                                <CheckCircle className="text-txt-success-primary" />
                                {permission.description}
                              </p>
                            ) : (
                              ""
                            ),
                          )}
                        </div>
                      </Box>
                    ) : (
                      ""
                    )
                  }
                />

                {role && (
                  <div
                    className="px-2 cursor-pointer hover:bg-bg-primary-hover"
                    onClick={() => setShowRoleInfo(true)}
                  >
                    <Flex direction="column" className="gap-1 p-2">
                      <Flex align="center" className="gap-2">
                        <User className="text-txt-tertiary" strokeWidth={2} />
                        <p className="text-sm font-semibold text-txt-tertiary">
                          {role}
                        </p>
                      </Flex>
                    </Flex>
                  </div>
                )}

                <Link to="/logout">
                  <Flex className="py-2 px-4 gap-2 rounded-b-lg hover:bg-bg-primary-hover">
                    <LogOut color="#D92D20" strokeWidth={2} />
                    <p className="text-sm font-semibold text-txt-secondary">
                      Log out
                    </p>
                  </Flex>
                </Link>
              </PopoverContent>
            </Popover>
          ) : (
            <div className="m-auto pb-4">
              <UserCircle height={24} width={24} />
            </div>
          )}
        </SidebarFooter>
      </SidebarMenu>
    </Flex>
  );
}
